export const getAllProductImages = (
  product: any,
  resolvedSelectedColor: string,
  colorVariants: any[],
  variants: any[],
  selectedOption?: string | null
) => {
  if (!product) return [];

  const resolve = (url?: string | null) => resolveProductImage(url);
  const normalizeColor = (value: unknown) => String(value || '').trim().toLowerCase();
  const normalizeOption = (value: unknown) => String(value || '').trim().toLowerCase();

  // 1. Collect gallery images from global product gallery structure
  const galleryImages = (product.product_images || product.images || [])
    .map((img: any) => (typeof img === 'string' ? img : img.image_url))
    .filter(Boolean)
    .map(resolve);

  // 2. Collect color-specific and model-specific images from variant_groups
  const selectedColorKey = normalizeColor(resolvedSelectedColor);
  const selectedOptionKey = selectedOption ? normalizeOption(selectedOption) : '';

  const colorGroups = (product.variant_groups || []).filter(
    (group: any) => normalizeColor(group?.color) === selectedColorKey
  );

  let targetGroupImages: string[] = [];

  if (colorGroups.length > 0) {
    if (colorGroups.length === 1) {
      // Single group for this color (standard color or single model)
      targetGroupImages = (colorGroups[0].images || [])
        .map((img: any) => (typeof img === 'string' ? img : img?.image_url))
        .filter(Boolean)
        .map(resolve);
    } else {
      // Multiple groups for this color (e.g. Berlengan vs Tanpa Lengan)
      // Check matching by active variant's group_id or group_name
      const activeVariant = (colorVariants || []).find(
        (v: any) => normalizeOption(v.option_name || v.option) === selectedOptionKey
      );

      let matchedGroup = null;
      if (activeVariant && activeVariant.group_id) {
        matchedGroup = colorGroups.find((g: any) => g.id === activeVariant.group_id);
      }
      if (!matchedGroup && selectedOptionKey) {
        matchedGroup = colorGroups.find(
          (g: any) => g.group_name && normalizeOption(g.group_name) === selectedOptionKey
        );
      }

      if (matchedGroup && Array.isArray(matchedGroup.images) && matchedGroup.images.length > 0) {
        targetGroupImages = matchedGroup.images
          .map((img: any) => (typeof img === 'string' ? img : img?.image_url))
          .filter(Boolean)
          .map(resolve);
      } else {
        // Fallback: take first group's images, or combine all
        const firstWithImgs = colorGroups.find((g: any) => Array.isArray(g.images) && g.images.length > 0);
        if (firstWithImgs) {
          targetGroupImages = firstWithImgs.images
            .map((img: any) => (typeof img === 'string' ? img : img?.image_url))
            .filter(Boolean)
            .map(resolve);
        } else {
          targetGroupImages = colorGroups
            .flatMap((g: any) => (g.images || []).map((img: any) => (typeof img === 'string' ? img : img?.image_url)))
            .filter(Boolean)
            .map(resolve);
        }
      }
    }
  }

  // 3. Collect legacy color-specific variant images as fallback
  const variantImages = (colorVariants || [])
    .map((variant: any) => variant.image_url || variant.image)
    .filter(Boolean)
    .map(resolve);

  // Combine: product gallery first, then selected color/model group gallery, then legacy variant images
  const combined = [...galleryImages, ...targetGroupImages, ...variantImages];
  const uniqueImages = Array.from(new Set(combined)) as string[];

  // 4. Ensure the thumbnail is included and at the very front
  const thumb = resolve(product?.thumbnail_url || product?.thumbnailUrl || product?.image_url || product?.image);
  if (thumb) {
    const thumbIndex = uniqueImages.indexOf(thumb);
    if (thumbIndex > -1) {
      uniqueImages.splice(thumbIndex, 1);
    }
    uniqueImages.unshift(thumb);
  }

  // 5. Fallback to all variant/group images if still empty
  if (uniqueImages.length === 0) {
    const allGroupImages = (product.variant_groups || [])
      .flatMap((group: any) => (group.images || []).map((img: any) => (typeof img === 'string' ? img : img?.image_url)))
      .filter(Boolean)
      .map(resolve);
    const allVariantImages = (variants || []).map((v: any) => v.image_url || v.image).filter(Boolean).map(resolve);
    return Array.from(new Set([...allGroupImages, ...allVariantImages])) as string[];
  }

  return uniqueImages;
};

/**
 * Resolves a product image URL, handling relative paths and providing a reliable fallback.
 */
export const resolveProductImage = (url?: string | null) => {
  if (!url || typeof url !== 'string' || url.trim() === '') {
    return '';
  }

  const trimmedUrl = url.trim();

  // 1. Absolute URLs
  if (trimmedUrl.startsWith('http')) {
    return trimmedUrl;
  }

  // 2. Data URLs
  if (trimmedUrl.startsWith('data:')) {
    return trimmedUrl;
  }

  // 3. Already starts with /uploads or uploads/
  if (trimmedUrl.startsWith('/uploads/')) {
    return trimmedUrl;
  }
  if (trimmedUrl.startsWith('uploads/')) {
    return `/${trimmedUrl}`;
  }

  // 4. Handle leading slash but not followed by uploads (could be another public asset)
  if (trimmedUrl.startsWith('/')) {
    // If it's a filename in root, check if we should assume uploads
    // But for now, respect the leading slash as an absolute path from root
    return trimmedUrl;
  }

  // 5. Raw filenames (e.g. "abc.webp")
  // These are most likely uploads if they don't have a slash
  if (!trimmedUrl.includes('/')) {
    return `/uploads/${trimmedUrl}`;
  }

  // Fallback: prepend slash if missing
  return `/${trimmedUrl}`;
};

/**
 * Generates a responsive srcset attribute for derived images.
 */
export const getProductImageSrcSet = (url?: string | null) => {
  if (!url || typeof url !== 'string' || !url.includes('/uploads/derived/')) {
    return undefined;
  }
  const match = url.match(/(.+)-w(\d+)\.webp$/);
  if (!match) return undefined;
  const base = match[1];
  const maxWidth = parseInt(match[2], 10);

  // Dynamically map to the exact width lists generated by backend profiles
  let widths: number[];
  if (maxWidth === 1440) {
    // product_detail
    widths = [720, 1080, 1440];
  } else if (maxWidth === 1080) {
    // product_listing
    widths = [480, 720, 1080];
  } else if (maxWidth === 2000) {
    // generic
    widths = [480, 768, 1080, 1440, 2000];
  } else if (maxWidth === 1920 || maxWidth === 1280) {
    // hero_16x9
    widths = [1280, 1920];
  } else {
    // fallback filter logic
    widths = [480, 720, 1080, 1440, 2000].filter(w => w <= maxWidth);
    if (!widths.includes(maxWidth)) {
      widths.push(maxWidth);
    }
  }

  return widths.map(w => `${base}-w${w}.webp ${w}w`).join(', ');
};
